import { Injectable } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { ComponentType } from "@angular/cdk/portal";
import { Observable } from "rxjs";

/**
 * Abstract base service for modal dialogs.
 *
 * @template T - The component type of the modal
 * @template R - The result type returned when the modal closes
 *
 * @example
 * ```typescript
 * @Injectable({ providedIn: 'root' })
 * export class MyModalService extends BaseModalService<MyModalComponent, MyModalResult> {
 *   constructor(dialog: MatDialog) {
 *     super(dialog);
 *   }
 *
 *   protected getDefaultConfig(): MatDialogConfig {
 *     return {
 *       width: '500px',
 *       maxWidth: '90vw',
 *       disableClose: false,
 *       autoFocus: true,
 *       restoreFocus: true,
 *     };
 *   }
 * }
 * ```
 */
@Injectable()
export abstract class BaseModalService<T, R> {
  constructor(protected readonly dialog: MatDialog) {}

  /**
   * Opens a modal dialog with the specified component, data, and configuration.
   *
   * @param component - The component to display in the modal
   * @param data - Data to pass to the modal component
   * @param config - Optional configuration to override defaults
   * @returns Observable that emits the result when the modal closes
   */
  protected openModal(
    component: ComponentType<T>,
    data: any,
    config?: MatDialogConfig,
  ): Observable<R | null | undefined> {
    const dialogRef = this.dialog.open(component, {
      ...this.getDefaultConfig(),
      ...config,
      data,
    });

    return dialogRef.afterClosed();
  }

  /**
   * Returns the default configuration for modals opened by this service.
   * Override this method to provide service-specific defaults.
   *
   * @returns The default MatDialogConfig
   */
  protected abstract getDefaultConfig(): MatDialogConfig;
}
